import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { displayToast } from "../../../components/app/AppToast";
import { RequestError } from "../../../../../api/src/shared/request";
import {
  apiClient,
  useApiQuery,
  useApiRevalidate,
} from "../../../services/api";
import type { ConversationEngine } from "../../assistant/conversationEngine";
import type { EnsureGptConversation } from "./useEnsureGptConversation";

// Referencing Juisci content into a Juisci GPT conversation — a playlist or an
// article (the "+" menu, or an `@`/`+` composer mention). The exact twin of
// `useChatAttachments`, minus upload/preview: a reference is a persisted,
// conversation-scoped grounding source whose content the agent resolves
// server-side each turn.
//
// The two kinds share one hook because they share one server resource
// (`ConversationReference`) and one lifecycle; they differ only in what the
// picker offers and what the agent does with them (a playlist is a corpus to
// search, an article is a paper to read — that split lives on the server).
//
// Adding is IMMEDIATE (a `ConversationReference` row FK'd to the conversation),
// so referencing on the LANDING page creates the conversation first — through
// the SAME `conversation` coordinator `useChatAttachments` uses, so a file and a
// reference added together create ONE conversation, not two.
//
// The referenced list is NOT local state: it is the references query, refetched
// after every mutation. Only the in-flight adds live here (they have no server
// id yet) — one source of truth for "what is referenced".

export type ChatReferenceKind = "playlist" | "article";

export type SettledChatReference = {
  status: "referenced";
  refId: string;
  kind: ChatReferenceKind;
  targetId: string;
  title: string;
  // The article's CURRENT slug, resolved server-side on every list (an article is
  // routed by slug, not by id — `/post/<id>` 404s). Null for a playlist (linked
  // by id) and for an article deleted since it was referenced: exactly the two
  // cases where the chip must not link anywhere.
  slug: string | null;
};

export type ChatReferenceItem =
  // `addId` scopes an in-flight add to its own optimistic chip (a target can be
  // referenced only once, but the optimistic row precedes the server id).
  | {
      status: "adding";
      addId: number;
      kind: ChatReferenceKind;
      targetId: string;
      title: string;
    }
  | SettledChatReference;

export type ChatReferences = {
  references: ChatReferenceItem[];
  // An add or a remove is in flight — the composer disables sending, because a
  // question asked before the reference lands would be answered without it.
  isBusy: boolean;
  // Resolves true once this call's reference has landed; false when it added
  // nothing (already there, failed with a toast, or overtaken by a new
  // conversation). A caller that needs the paper referenced before it asks
  // (the "About this study" hand-off) chains on it.
  onAdd: (
    kind: ChatReferenceKind,
    targetId: string,
    title: string
  ) => Promise<boolean>;
  onRemove: (refId: string) => void;
  reset: () => void;
  // Every target currently referenced OR being added, as `kind:id` keys — the
  // pickers grey these out so the same target is not referenced twice (the
  // server 409s, but the UI should not offer it). Keyed by kind because a
  // playlist id and an article id live in different tables.
  referencedKeys: string[];
};

/** The identity of a reference target across both kinds. */
export function referenceKey(
  kind: ChatReferenceKind,
  targetId: string
): string {
  return `${kind}:${targetId}`;
}

function referenceErrorMessage(
  error: unknown,
  t: (key: string) => string
): string {
  return error instanceof RequestError
    ? error.message
    : t("Something went wrong. Please try again.");
}

export function useChatReferences(opts: {
  conversation: EnsureGptConversation;
}): ChatReferences {
  const { conversation } = opts;
  const { t } = useTranslation();
  const addCounter = useRef(0);
  const revalidate = useApiRevalidate();
  // Local rows for adds in flight (no server id yet). `addId` keys them; the
  // kind + target + title are for the optimistic chip.
  const [adding, setAdding] = useState<
    Array<{
      addId: number;
      kind: ChatReferenceKind;
      targetId: string;
      title: string;
    }>
  >([]);
  const [busyCount, setBusyCount] = useState(0);
  const target = conversation.target;

  // One query per engine; the other stays idle.
  const legacyReferencesQuery = useApiQuery(
    "/webapp/private/conversations/:id/references",
    target === null || target.engine !== "legacy"
      ? false
      : { param: { id: target.id } }
  );
  const assistantReferencesQuery = useApiQuery(
    "/assistant/conversations/:conversationId/references",
    target === null || target.engine !== "assistant"
      ? false
      : { param: { conversationId: target.id } }
  );
  const rows =
    legacyReferencesQuery.state === "success"
      ? legacyReferencesQuery.body
      : assistantReferencesQuery.state === "success"
        ? assistantReferencesQuery.body.references
        : [];
  const referenced: SettledChatReference[] = rows.map((row) => ({
    status: "referenced",
    refId: row.id,
    kind: row.kind,
    targetId: row.targetId,
    title: row.title,
    slug: row.slug,
  }));
  const revalidateReferences = (id: string, engine: ConversationEngine) =>
    engine === "assistant"
      ? revalidate("/assistant/conversations/:conversationId/references", {
          param: { conversationId: id },
        })
      : revalidate("/webapp/private/conversations/:id/references", {
          param: { id },
        });

  const onAdd = (
    kind: ChatReferenceKind,
    targetId: string,
    title: string
  ): Promise<boolean> => {
    // Ignore a second tap on an already-referenced/in-flight target — the server
    // would 409 and the pickers grey it out, but guard here too.
    const key = referenceKey(kind, targetId);
    const alreadyThere =
      referenced.some((ref) => referenceKey(ref.kind, ref.targetId) === key) ||
      adding.some((row) => referenceKey(row.kind, row.targetId) === key);
    if (alreadyThere) return Promise.resolve(false);
    addCounter.current += 1;
    const addId = addCounter.current;
    setAdding((current) => [...current, { addId, kind, targetId, title }]);
    setBusyCount((count) => count + 1);
    const epoch = conversation.epoch();

    return (async (): Promise<boolean> => {
      try {
        const ensured = await conversation.ensure(epoch);
        if (conversation.isStale(epoch)) return false;
        if (ensured.engine === "assistant")
          await apiClient.post(
            "/assistant/conversations/:conversationId/references",
            {
              param: { conversationId: ensured.id },
              json: { kind, targetId },
            }
          );
        else
          await apiClient.post("/webapp/private/conversations/:id/references", {
            param: { id: ensured.id },
            json: { kind, targetId },
          });
        if (conversation.isStale(epoch)) return false;
        // The query owns the referenced list — invalidate rather than splicing
        // the response in, so the chips never drift from the conversation's real
        // context. AWAITED, so the cache holds the new row before the optimistic
        // one clears below and there is no frame with neither.
        await revalidateReferences(ensured.id, ensured.engine);
        return !conversation.isStale(epoch);
      } catch (error) {
        if (conversation.isStale(epoch)) return false;
        displayToast(referenceErrorMessage(error, t), "error");
        return false;
      } finally {
        if (!conversation.isStale(epoch)) {
          setAdding((current) => current.filter((row) => row.addId !== addId));
          setBusyCount((count) => count - 1);
        }
      }
    })();
  };

  const onRemove = (refId: string): void => {
    if (target === null)
      throw new Error(
        "should not happen: removing a reference with no conversation"
      );
    const epoch = conversation.epoch();
    setBusyCount((count) => count + 1);
    void (async () => {
      try {
        if (target.engine === "assistant")
          await apiClient.delete(
            "/assistant/conversations/:conversationId/references/:referenceId",
            { param: { conversationId: target.id, referenceId: refId } }
          );
        else
          await apiClient.delete(
            "/webapp/private/conversations/:id/references/:referenceId",
            { param: { id: target.id, referenceId: refId } }
          );
        if (conversation.isStale(epoch)) return;
        await revalidateReferences(target.id, target.engine);
      } catch (error) {
        if (conversation.isStale(epoch)) return;
        displayToast(referenceErrorMessage(error, t), "error");
      } finally {
        if (!conversation.isStale(epoch)) setBusyCount((count) => count - 1);
      }
    })();
  };

  const reset = (): void => {
    setBusyCount(0);
    setAdding([]);
    // Bump the shared epoch + forget the created conversation. Idempotent with the
    // attachment hook's reset — the epoch just increments again.
    conversation.reset();
  };

  const items: ChatReferenceItem[] = [
    ...referenced,
    ...adding.map((row) => ({
      status: "adding" as const,
      addId: row.addId,
      kind: row.kind,
      targetId: row.targetId,
      title: row.title,
    })),
  ];

  return {
    references: items,
    isBusy: busyCount > 0,
    onAdd,
    onRemove,
    reset,
    referencedKeys: items.map((item) => referenceKey(item.kind, item.targetId)),
  };
}
